<script lang="ts">
	// UC-34. The restaurant's discounts, as the Owner Dashboard - Menu page draws D21:
	// one list for every shop, each discount a percent or an amount, edited in
	// place one row at a time, and beside it what a discount does.
	//
	// Removing is undone, not confirmed: the row leaves at once and the server
	// hears of it when the Undo runs out (owner/notices.svelte.ts). A percent is
	// a whole number; an amount is read as a price is, so 10 is 10.00
	// (owner/price.svelte.ts). The preview takes the discount off 87.00 with the
	// same rule the till and the server use (domain/pricing.ts).

	import Band from '../components/Band.svelte';
	import Button from '../components/Button.svelte';
	import Card from '../components/Card.svelte';
	import Field from '../components/Field.svelte';
	import Loading from '../components/Loading.svelte';
	import Segmented from '../components/Segmented.svelte';
	import { api } from '../api/client';
	import { fill, message, type Language } from '../i18n/messages';
	// How the till says a discount, its figure kept left to right inside
	// Arabic, so the owner's list says it the same way.
	import { discountRate } from '../format/discount';
	import { leftToRight } from '../format/bidi';
	import { formatMinor, parsePrice } from '../format/money';
	import { discountMinor, MAX_DISCOUNT_PERCENT, type DiscountKind } from '../domain/pricing';
	import {
		loadDiscounts,
		ownerDiscounts,
		setDiscountEditing,
		type Discount,
	} from '../owner/data.svelte';
	import { removeWithUndo } from '../owner/notices.svelte';
	import { PriceInput } from '../owner/price.svelte';

	interface Props {
		language: Language;
		minorDigits: number;
		/** Bumped by the title row's "New discount". */
		actionAsked: number;
	}

	let { language, minorDigits, actionAsked }: Props = $props();

	const NAME_LENGTH = 40;
	const VALUE_LENGTH = 14;

	const list = $derived(ownerDiscounts());

	$effect(() => {
		void loadDiscounts(message(language, 'loadFailed'));
	});

	interface Draft {
		/** The discount being changed, or 'new'. */
		id: string;
		name: string;
		kind: DiscountKind;
		percent: string;
		amount: PriceInput;
		nameError: string;
		valueError: string;
	}

	let draft = $state<Draft | null>(null);
	let saving = $state(false);
	let failure = $state('');

	$effect(() => {
		if (actionAsked > 0) start(null);
	});

	// The title row's "New discount" waits while a row is open.
	$effect(() => {
		setDiscountEditing(draft !== null);
		return () => setDiscountEditing(false);
	});

	function start(d: Discount | null) {
		const amount = new PriceInput(() => minorDigits);
		amount.load(d?.kind === 'amount' ? d.value : null);
		draft = {
			id: d?.id ?? 'new',
			name: d?.name ?? '',
			kind: d?.kind === 'amount' ? 'amount' : 'percent',
			percent: d?.kind === 'percent' ? String(d.value) : '',
			amount,
			nameError: '',
			valueError: '',
		};
		failure = '';
	}

	/** The percent as a whole number from 1 to 100, or null. */
	function percentOf(text: string): number | null {
		const read = parsePrice(text, 0);
		return read.ok && read.minor >= 1 && read.minor <= MAX_DISCOUNT_PERCENT ? read.minor : null;
	}

	/** What the draft would save, or null while it is not a discount yet. */
	function valueOf(d: Draft): number | null {
		if (d.kind === 'percent') return percentOf(d.percent);
		const minor = d.amount.minor;
		return minor !== null && minor >= 1 ? minor : null;
	}

	// A percent says what is wrong as it is typed; an amount, as a price does,
	// once the field is left, and an amount of 0.00 takes nothing off.
	function valueError(d: Draft): string {
		if (d.valueError) return d.valueError;
		if (d.kind === 'percent') {
			if (d.percent.trim() === '') return '';
			return percentOf(d.percent) === null ? message(language, 'discountsPercentRange') : '';
		}
		const price = d.amount.error(language);
		if (price) return price;
		return d.amount.checked && d.amount.minor === 0 ? message(language, 'priceInvalid') : '';
	}

	/** The name as it will be saved: trimmed, its inner spaces collapsed. */
	function nameOf(d: Draft): string {
		return d.name.trim().replace(/\s+/g, ' ');
	}

	const sample = $derived(87 * 10 ** minorDigits);

	function preview(d: Draft): string {
		const value = valueOf(d);
		if (value === null) return '';
		const off = discountMinor(sample, d.kind, value, minorDigits);
		return `${message(language, 'discountsOnTill')} ${nameOf(d) || '—'} · ${discountRate(language, d.kind, value, minorDigits)}. ${fill(
			message(language, 'discountsTakesOn'),
			leftToRight(formatMinor(sample, minorDigits)),
			leftToRight(formatMinor(off, minorDigits)),
		)}`;
	}

	async function save() {
		const d = draft;
		if (!d || saving) return;
		if (d.kind === 'amount') d.amount.tidy();
		const value = valueOf(d);
		const name = nameOf(d);
		if (!name || value === null) return;
		saving = true;
		failure = '';
		d.nameError = '';
		d.valueError = '';
		const body = { name, kind: d.kind, value };
		try {
			const { error } =
				d.id === 'new'
					? await api.POST('/api/owner/discounts', { body })
					: await api.PATCH('/api/owner/discounts/{discountId}', {
							params: { path: { discountId: d.id } },
							body,
						});
			if (error !== undefined) {
				const field = (error.details as { field?: string } | undefined)?.field;
				if (error.code === 'DISCOUNT_NAME_IN_USE') {
					d.nameError = message(language, 'discountsNameTaken');
				} else if (error.code === 'VALIDATION_ERROR' && field === 'value') {
					d.valueError =
						d.kind === 'percent'
							? message(language, 'discountsPercentRange')
							: (error.message ?? message(language, 'loadFailed'));
				} else if (error.code === 'VALIDATION_ERROR' && field === 'name') {
					d.nameError = error.message ?? message(language, 'loadFailed');
				} else {
					failure = error.message ?? message(language, 'loadFailed');
				}
				return;
			}
			draft = null;
			await loadDiscounts(message(language, 'loadFailed'));
		} catch {
			failure = message(language, 'loadFailed');
		} finally {
			saving = false;
		}
	}

	function remove(d: Discount) {
		const at = list.value.indexOf(d);
		list.value = list.value.filter((x) => x.id !== d.id);
		removeWithUndo(
			fill(message(language, 'discountsRemoved'), d.name),
			async () => {
				try {
					await api.POST('/api/owner/discounts/{discountId}/remove', {
						params: { path: { discountId: d.id } },
					});
				} finally {
					await loadDiscounts(message(language, 'loadFailed'));
				}
			},
			() => {
				list.value.splice(at, 0, d);
			},
		);
	}

	// A new discount opens at the top of the list, as D21 draws it.
	const rows = $derived([
		...(draft?.id === 'new' ? [{ id: 'new', name: '', kind: 'percent' as const, value: 0 }] : []),
		...list.value,
	]);

	const kinds = $derived([
		{
			value: 'percent',
			label: message(language, 'discountsPercent'),
			testId: 'discount-kind-percent',
		},
		{
			value: 'amount',
			label: message(language, 'discountsAmount'),
			testId: 'discount-kind-amount',
		},
	]);
</script>

<div class="discounts" data-testid="panel-discounts">
	<p class="intro">{message(language, 'discountsIntro')}</p>

	{#if failure || list.failure}
		<Band tone="flag" testId="discounts-error">{failure || list.failure}</Band>
	{/if}

	{#if !list.loaded}
		<div class="wait">
			<Loading
				size="lg"
				label={message(language, 'discountsLoading')}
				caption={message(language, 'discountsLoading')}
			/>
		</div>
	{:else}
		<div class="grid">
			<Card
				title={message(language, 'ownerDiscounts')}
				note={fill(message(language, 'discountsCount'), leftToRight(String(list.value.length)))}
				testId="discounts-list"
			>
				{#if list.value.length === 0 && draft?.id !== 'new'}
					<p class="empty" data-testid="discounts-empty">{message(language, 'discountsEmpty')}</p>
				{/if}
				{#each rows as d (d.id)}
					<div class="row" data-testid="discount-{d.id}">
						{#if draft && draft.id === d.id}
							<Field
								testId="discount-name"
								label={message(language, 'discountsName')}
								value={draft.name}
								maxLength={NAME_LENGTH}
								error={draft.nameError}
								errorTestId="discount-name-error"
								onInput={(v) => {
									if (!draft) return;
									draft.name = v;
									draft.nameError = '';
								}}
							/>
							<div class="value">
								<div class="kind">
									<span class="label">{message(language, 'discountsKind')}</span>
									<Segmented
										testId="discount-kind"
										label={message(language, 'discountsKind')}
										options={kinds}
										value={draft.kind}
										onChange={(v) => {
											if (!draft) return;
											const kind = v === 'amount' ? 'amount' : 'percent';
											if (kind === draft.kind) return;
											// A new kind starts its value afresh: 20 percent is not 20.00.
											draft.kind = kind;
											draft.percent = '';
											draft.amount.load(null);
											draft.valueError = '';
										}}
									/>
								</div>
								<div class="amount">
									{#if draft.kind === 'percent'}
										<Field
											testId="discount-value"
											label={message(language, 'discountsPercentLabel')}
											value={draft.percent}
											note={message(language, 'discountsPercentNote')}
											error={valueError(draft)}
											errorTestId="discount-value-error"
											inputMode="numeric"
											maxLength={VALUE_LENGTH}
											mono
											onInput={(v) => {
												if (!draft) return;
												draft.percent = v;
												draft.valueError = '';
											}}
										/>
									{:else}
										<Field
											testId="discount-value"
											label={message(language, 'discountsAmountLabel')}
											value={draft.amount.text}
											note={message(language, 'discountsAmountNote')}
											error={valueError(draft)}
											errorTestId="discount-value-error"
											inputMode="decimal"
											maxLength={VALUE_LENGTH}
											mono
											onInput={(v) => {
												if (!draft) return;
												draft.amount.set(v);
												draft.valueError = '';
											}}
											onBlur={() => draft?.amount.tidy()}
										/>
									{/if}
								</div>
							</div>
							{#if preview(draft)}
								<p class="preview" data-testid="discount-preview">{preview(draft)}</p>
							{/if}
							<div class="actions">
								<Button
									testId="discount-cancel"
									label={message(language, 'cancel')}
									tone="text-quiet"
									size="sm"
									disabled={saving}
									onPress={() => (draft = null)}
								/>
								<Button
									testId="discount-save"
									label={message(
										language,
										draft.id === 'new' ? 'discountsCreate' : 'discountsSave',
									)}
									size="sm"
									state={saving ? 'pending' : 'idle'}
									pendingLabel={message(language, 'saving')}
									disabled={!draft.name.trim() || valueOf(draft) === null || !!draft.nameError}
									onPress={() => void save()}
								/>
							</div>
						{:else}
							<div class="view">
								<span class="what">
									<span class="name">{d.name}</span>
									<span class="rate">· {discountRate(language, d.kind, d.value, minorDigits)}</span>
								</span>
								<Button
									testId="discount-edit-{d.id}"
									label={message(language, 'edit')}
									tone="text"
									size="sm"
									disabled={draft !== null || saving}
									onPress={() => start(d)}
								/>
								<Button
									testId="discount-remove-{d.id}"
									label={message(language, 'remove')}
									tone="text-quiet"
									size="sm"
									disabled={draft !== null || saving}
									onPress={() => remove(d)}
								/>
							</div>
						{/if}
					</div>
				{/each}
			</Card>

			<Card title={message(language, 'discountsHowTitle')} testId="discounts-how">
				<ul class="how">
					<li>{message(language, 'discountsHow1')}</li>
					<li>{message(language, 'discountsHow2')}</li>
					<li>{message(language, 'discountsHow3')}</li>
					<li>{message(language, 'discountsHow4')}</li>
					<li>{message(language, 'discountsHow5')}</li>
				</ul>
			</Card>
		</div>
	{/if}
</div>

<style>
	.discounts {
		display: flex;
		flex-direction: column;
		gap: var(--space-9);
	}

	.intro {
		margin: 0;
		max-inline-size: var(--measure);
		font-size: var(--text-sm);
		line-height: var(--leading-snug);
		color: var(--text-body);
		text-wrap: pretty;
	}

	.wait {
		display: flex;
		justify-content: center;
		padding-block: var(--space-12);
	}

	.grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(min(100%, 360px), 1fr));
		gap: var(--grid-gap);
		align-items: start;
	}

	.empty {
		margin: 0;
		padding: var(--card-pad);
		font-size: var(--text-sm);
		line-height: var(--leading-snug);
		color: var(--text-body);
		text-wrap: pretty;
	}

	.row {
		display: flex;
		flex-direction: column;
		gap: var(--space-5);
		padding: var(--row-pad);
		border-block-end: var(--border-width) solid var(--border-inset);
	}

	.view {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--space-2) var(--space-4);
	}

	.what {
		flex: 1 1 160px;
		min-inline-size: 0;
		display: flex;
		flex-wrap: wrap;
		align-items: baseline;
		gap: 0 var(--space-2);
		font-size: var(--text-sm);
	}

	.name {
		font-weight: 600;
		color: var(--text-strong);
	}

	.rate {
		color: var(--text-body);
		white-space: nowrap;
	}

	.value {
		display: flex;
		flex-wrap: wrap;
		align-items: flex-start;
		gap: var(--space-5);
	}

	.kind {
		flex: none;
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
	}

	.label {
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	.amount {
		flex: 1 1 180px;
		min-inline-size: 0;
	}

	.preview {
		margin: 0;
		font-size: var(--text-sm);
		line-height: var(--leading-snug);
		color: var(--text-body);
	}

	.actions {
		display: flex;
		flex-wrap: wrap;
		justify-content: flex-end;
		gap: var(--space-3);
	}

	.how {
		margin: 0;
		padding-block: var(--card-pad);
		padding-inline: calc(var(--card-pad) + 1.2em) var(--card-pad);
		display: flex;
		flex-direction: column;
		gap: var(--space-4);
		font-size: var(--text-sm);
		line-height: var(--leading-snug);
		color: var(--text-body);
		text-wrap: pretty;
	}
</style>
